/**
 * How likely a connected number is to stay connected — a heuristic, not a prediction.
 *
 * This is deliberately named "connection health," never "ban risk": nothing in this product
 * observes the signals a real ban-risk model would need — message-block rates, spam reports,
 * WhatsApp's own trust scoring — because WhatsApp does not expose any of that to a third
 * party. What it does expose, indirectly, is the session host's own connection state, which
 * is genuine signal: a number that keeps dropping and reconnecting is at real, elevated risk
 * of ending up logged out, whatever the underlying cause. Scoring that honestly is worth
 * doing; dressing it up as something that predicts a WhatsApp ban would not be.
 *
 * One second-hand signal was added later and is worth naming, because it is the only input
 * here that reflects WhatsApp's opinion rather than ours: the share of a number's recent
 * bulk sends that came back refused. It is still not ban risk — a rejected send has many
 * causes — but a number whose sends stop landing is one to slow down, and that is now what
 * happens, because `warmup.ts` reads the tier this returns and shrinks the day's cap.
 */

export type HealthTier = 'healthy' | 'watch' | 'at_risk'

export interface NumberHealth {
  /** 0 (worst) to 100 (best). */
  score: number
  tier: HealthTier
  reason: string
}

/** What a number's recent bulk sends did, which is the only outcome signal we own. */
export interface RecentSends {
  attempted: number
  failed: number
}

export interface NumberHealthInput {
  sessionStatus: string | null
  lastConnectedAt: Date | null
  now: Date
  /**
   * Optional because most callers only display a number's state. The pacing path passes it,
   * and it is the one input here that is about WhatsApp's opinion of the number rather than
   * about our own socket: a run whose sends keep coming back rejected is the closest thing
   * to a warning WhatsApp gives a third party before it acts.
   */
  recentSends?: RecentSends
}

const DAY_MS = 86_400_000

/**
 * Below this a failure rate says nothing. Three failures out of four is a bad afternoon when
 * four is the whole sample; the same rate over sixty sends is a number in trouble.
 */
const MIN_OUTCOME_SAMPLE = 20
const FAILING_HARD = 0.5
const FAILING_SOFT = 0.2

/** Degrades a connected number whose sends are being refused, or `null` if they are fine. */
function failureSignal(recent: RecentSends | undefined): NumberHealth | null {
  if (!recent || recent.attempted < MIN_OUTCOME_SAMPLE) return null

  const rate = recent.failed / recent.attempted
  const percent = Math.round(rate * 100)

  if (rate >= FAILING_HARD) {
    return {
      score: 30,
      tier: 'at_risk',
      reason: `Connected, but ${percent}% of its recent sends were refused. Something is rejecting messages from this number.`,
    }
  }
  if (rate >= FAILING_SOFT) {
    return {
      score: 60,
      tier: 'watch',
      reason: `Connected, but ${percent}% of its recent sends were refused.`,
    }
  }
  return null
}

export function scoreNumberHealth(input: NumberHealthInput): NumberHealth {
  const { sessionStatus, lastConnectedAt, now } = input
  const daysSinceConnected = lastConnectedAt
    ? Math.floor((now.getTime() - lastConnectedAt.getTime()) / DAY_MS)
    : null

  if (sessionStatus === 'connected') {
    return failureSignal(input.recentSends) ?? { score: 100, tier: 'healthy', reason: 'Connected.' }
  }

  if (sessionStatus === 'logged_out') {
    return {
      score: 10,
      tier: 'at_risk',
      reason: 'Unlinked from WhatsApp. It needs to be paired again before it can send or receive.',
    }
  }

  if (sessionStatus === 'failed') {
    return {
      score: 15,
      tier: 'at_risk',
      reason: 'The connection failed and could not recover on its own.',
    }
  }

  if (sessionStatus === 'reconnecting') {
    // A number that reconnects and settles within the same day is ordinary network noise; one
    // that has been down and cycling for several is a real pattern worth surfacing.
    const score = daysSinceConnected === null ? 40 : Math.max(20, 55 - daysSinceConnected * 5)
    return {
      score,
      tier: score >= 50 ? 'watch' : 'at_risk',
      reason:
        daysSinceConnected === null
          ? 'Reconnecting. WhatsApp closed the connection and it is retrying.'
          : `Reconnecting, and has been unstable for ${daysSinceConnected} day${daysSinceConnected === 1 ? '' : 's'}.`,
    }
  }

  if (sessionStatus === 'awaiting_qr' || sessionStatus === 'starting') {
    return { score: 70, tier: 'watch', reason: 'Pairing in progress.' }
  }

  if (lastConnectedAt === null) {
    return { score: 50, tier: 'watch', reason: 'Not yet connected.' }
  }

  // A known status is not currently reported at all — no session, but it was connected once.
  return {
    score: 30,
    tier: 'at_risk',
    reason:
      daysSinceConnected === null
        ? 'Not currently connected.'
        : `Not connected for ${daysSinceConnected} day${daysSinceConnected === 1 ? '' : 's'}.`,
  }
}
